/**
 * TreeSelectEditor
 * Configuration for tree-select control with hierarchy and lazy loading
 * Uses COMPOSITE PATTERN for tree structure and lazy loading config
 */

import React, { useCallback, useState } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  disabled?: boolean;
}

interface TreeSelectConfig {
  dataSource?: TreeNode[];
  multiple?: boolean;
  checkable?: boolean;
  expandable?: boolean;
  lazy?: boolean;
  lazyLoadUrl?: string;
  showSearch?: boolean;
  defaultExpandAll?: boolean;
}

/**
 * Visual tree option editor — add, remove, rename, and nest options
 */
function TreeOptionEditor({ nodes, onChange, depth = 0 }: {
  nodes: TreeNode[];
  onChange: (nodes: TreeNode[]) => void;
  depth?: number;
}) {
  const handleAdd = () => {
    onChange([...nodes, { id: `node-${Date.now()}`, label: `Option ${nodes.length + 1}`, children: [] }]);
  };

  const handleUpdate = (index: number, field: 'id' | 'label', val: string) => {
    onChange(nodes.map((n, i) => i === index ? { ...n, [field]: val } : n));
  };

  const handleRemove = (index: number) => {
    onChange(nodes.filter((_, i) => i !== index));
  };

  const handleChildrenChange = (index: number, children: TreeNode[]) => {
    onChange(nodes.map((n, i) => i === index ? { ...n, children } : n));
  };

  const handleAddChild = (index: number) => {
    const node = nodes[index];
    const children = [...(node.children || []), { id: `node-${Date.now()}`, label: `Child ${(node.children?.length || 0) + 1}`, children: [] }];
    handleChildrenChange(index, children);
  };

  return (
    <div style={{ paddingLeft: depth > 0 ? 16 : 0, borderLeft: depth > 0 ? '2px solid rgba(48,155,72,0.2)' : 'none' }}>
      {nodes.length === 0 && depth === 0 && (
        <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 8px' }}>No options yet. Add one below.</p>
      )}
      {nodes.map((node, i) => (
        <div key={node.id || i} style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              type="text"
              value={node.id}
              onChange={(e) => handleUpdate(i, 'id', e.target.value)}
              className="af-properties-tab__input"
              placeholder="value"
              title="Option value"
              style={{ flex: 1 }}
            />
            <input
              type="text"
              value={node.label}
              onChange={(e) => handleUpdate(i, 'label', e.target.value)}
              className="af-properties-tab__input"
              placeholder="label"
              title="Option label"
              style={{ flex: 1 }}
            />
            <button type="button" onClick={() => handleAddChild(i)}
              className="af-properties-tab__button af-properties-tab__button--primary"
              title="Add child option"
              style={{ padding: '4px 8px', fontSize: 11 }}>
              + Child
            </button>
            <button type="button" onClick={() => handleRemove(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              style={{ padding: '4px 8px', fontSize: 11 }}>
              Remove
            </button>
          </div>
          {node.children && node.children.length > 0 && (
            <TreeOptionEditor
              nodes={node.children}
              onChange={(children) => handleChildrenChange(i, children)}
              depth={depth + 1}
            />
          )}
        </div>
      ))}
      <button type="button" onClick={handleAdd}
        className="af-properties-tab__button af-properties-tab__button--primary"
        style={{ marginTop: 4, fontSize: 12 }}>
        + Add {depth > 0 ? 'Child' : 'Option'}
      </button>
    </div>
  );
}

function renderTreeSelectEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as TreeSelectConfig;

  const dataSource = config.dataSource || [];
  const multiple = config.multiple ?? false;
  const checkable = config.checkable ?? false;
  const expandable = config.expandable ?? true;
  const lazy = config.lazy ?? false;
  const lazyLoadUrl = config.lazyLoadUrl || '';
  const showSearch = config.showSearch ?? true;
  const defaultExpandAll = config.defaultExpandAll ?? false;

  const handleDataSourceChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      try {
        const parsed = JSON.parse(e.target.value);
        onConfigChange('dataSource', parsed);
      } catch {
        // Invalid JSON, ignore
      }
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Tree Select Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={multiple}
            onChange={(e) => onConfigChange('multiple', e.target.checked)}
          />
          Allow Multiple Selection
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={checkable}
            onChange={(e) => onConfigChange('checkable', e.target.checked)}
          />
          Show Checkboxes
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={expandable}
            onChange={(e) => onConfigChange('expandable', e.target.checked)}
          />
          Nodes Expandable
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={defaultExpandAll}
            onChange={(e) => onConfigChange('defaultExpandAll', e.target.checked)}
          />
          Expand All by Default
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showSearch}
            onChange={(e) => onConfigChange('showSearch', e.target.checked)}
          />
          Show Search
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={lazy}
            onChange={(e) => onConfigChange('lazy', e.target.checked)}
          />
          Enable Lazy Loading
        </label>
      </div>

      {lazy && (
        <div className="af-properties-tab__group">
          <label htmlFor="lazy-url-input" className="af-properties-tab__label">
            Lazy Load URL (API endpoint for child nodes)
          </label>
          <input
            id="lazy-url-input"
            type="text"
            value={lazyLoadUrl}
            onChange={(e) => onConfigChange('lazyLoadUrl', e.target.value)}
            placeholder="https://api.example.com/tree/nodes?parentId={parentId}"
            className="af-properties-tab__input"
            aria-label="Lazy load URL"
          />
          <p className="af-properties-tab__info">Use {'{parentId}'} to reference parent node ID</p>
        </div>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Options
        </label>
        <TreeOptionEditor
          nodes={dataSource}
          onChange={(nodes) => onConfigChange('dataSource', nodes)}
        />
      </div>
    </div>
  );
}

export const TreeSelectEditor: PropertyEditor = {
  type: 'tree-select',
  label: 'Tree Select',
  description: 'Configure hierarchical tree select with lazy loading support',
  render: renderTreeSelectEditor,
};
